import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import FreeTrial from "../components/LandingPage/FreeTrial";
import GlobalDataLoader from "../utils/GlobalDataLoader";

export const GlobalContext = createContext();

export const GlobalProvider = ({ children }) => {
  // ================= ENV CONFIG =================

  // const baseUrl = process.env.REACT_APP_ANLOOK_API_BASE_URL;
  const baseUrl = process.env.REACT_APP_ANLOOK_API_BASE_URL;
  const baseURL2 = "https://auth.seobix.com/api";
  const appName = process.env.REACT_APP_APP_NAME;

  const seobixBaseUrl = process.env.REACT_APP_SEOBIX_API_BASE_URL;
  const seobixUsername = process.env.REACT_APP_SEOBIX_API_AUTH_USERNAME;
  const seobixPassword = process.env.REACT_APP_SEOBIX_API_AUTH_PASSWORD;

  // Vendor / Admin Anlook API
  // Uses the existing SeoBix Basic Auth credentials.
  const anlookAdminBaseUrl = process.env.REACT_APP_ANLOOK_ADMIN_API_BASE_URL;

  //  const anlookBaseUrl = process.env.REACT_APP_ANLOOK_API_BASE_URL;
  const anlookBaseUrl = process.env.REACT_APP_ANLOOK_PHP_API_BASE_URL;
  const anlookUsername = process.env.REACT_APP_ANLOOK_API_AUTH_USERNAME;
  const anlookPassword = process.env.REACT_APP_ANLOOK_API_AUTH_PASSWORD;

  const bumppyBaseUrl =
    process.env.REACT_APP_BUMPPY_API_BASE_URL || "https://bumppy.in/anlook/api";

  const bumppyUsername = process.env.REACT_APP_BUMPPY_API_AUTH_USERNAME;

  const bumppyPassword = process.env.REACT_APP_BUMPPY_API_AUTH_PASSWORD;

  // Blog API Credentials
  const USERNAME = process.env.REACT_APP_BLOG_API_USERNAME;
  const PASSWORD = process.env.REACT_APP_BLOG_API_PASSWORD;

  // Sales API Basic Auth
  const salesUsername = process.env.REACT_APP_API_AUTH_USERNAME;
  const salesPassword = process.env.REACT_APP_API_AUTH_PASSWORD;

  const basicAuth = useMemo(() => {
    if (!salesUsername || !salesPassword) return "";

    return `Basic ${btoa(`${salesUsername}:${salesPassword}`)}`;
  }, [salesUsername, salesPassword]);

  const bumppyBasicAuth = useMemo(() => {
    if (!bumppyUsername || !bumppyPassword) {
      console.error("Bumppy API username or password missing");
      return "";
    }

    return `Basic ${btoa(`${bumppyUsername}:${bumppyPassword}`)}`;
  }, [bumppyUsername, bumppyPassword]);

  const seobixBasicAuth = useMemo(() => {
    if (!seobixUsername || !seobixPassword) {
      console.error("Seobix API username or password missing");
      return "";
    }

    return `Basic ${btoa(`${seobixUsername}:${seobixPassword}`)}`;
  }, [seobixUsername, seobixPassword]);

  // ================= USER =================

  const getStoredUser = () => {
    try {
      const storedUser = sessionStorage.getItem("user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
      console.error("User parse error:", error);
      sessionStorage.removeItem("user");
      return null;
    }
  };

  const [user, setUser] = useState(getStoredUser);
  const userID = user?.userid || null;

  const [power, setPower] = useState(() => sessionStorage.getItem("power"));

  // ================= API INSTANCE =================

  const authHeaders = useMemo(
    () => ({
      accept: "*/*",
      "Content-Type": "application/json",
      ...(basicAuth ? { Authorization: basicAuth } : {}),
      "X-App-Name": appName || "WebApp",
    }),
    [basicAuth, appName],
  );

  const api = useMemo(() => {
    const instance = axios.create({
      baseURL: baseUrl,
      headers: authHeaders,
      timeout: 30000,
    });

    instance.interceptors.request.use(
      (config) => {
        if (userID) {
          config.headers["USER-ID"] = String(userID);
          config.headers["X-User-Id"] = String(userID);
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => {
        const responseCode = String(response?.data?.responseCode || "");

        if (responseCode === "98" || responseCode === "401") {
          console.error(
            "Unauthorised API response:",
            response?.data?.responseMessage,
          );

          sessionStorage.removeItem("user");
          sessionStorage.removeItem("power");
          setUser(null);
        }

        return response;
      },
      (error) => {
        const status = error?.response?.status;

        if (status === 401) {
          console.error("Session expired. Please login again.");

          sessionStorage.removeItem("user");
          sessionStorage.removeItem("power");
          setUser(null);
        }

        return Promise.reject(error);
      },
    );

    return instance;
  }, [baseUrl, authHeaders, userID]);

  const bumppyApi = useMemo(() => {
    const instance = axios.create({
      baseURL: bumppyBaseUrl,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(bumppyBasicAuth
          ? {
              Authorization: bumppyBasicAuth,
            }
          : {}),
      },
      timeout: 30000,
    });

    instance.interceptors.request.use(
      (config) => {
        console.log(
          "Bumppy Request URL:",
          `${config.baseURL || ""}${config.url || ""}`,
        );

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Bumppy API error:",
          error?.response?.status,
          error?.response?.data,
        );

        return Promise.reject(error);
      },
    );

    return instance;
  }, [bumppyBaseUrl, bumppyBasicAuth]);

  const anlookBasicAuth = useMemo(() => {
    if (!anlookUsername || !anlookPassword) {
      console.error("Anlook API username or password missing");
      return "";
    }

    return `Basic ${window.btoa(`${anlookUsername}:${anlookPassword}`)}`;
  }, [anlookUsername, anlookPassword]);

  const anlookApi = useMemo(() => {
    const instance = axios.create({
      baseURL: anlookBaseUrl,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(anlookBasicAuth
          ? {
              Authorization: anlookBasicAuth,
            }
          : {}),
      },
      timeout: 30000,
    });

    instance.interceptors.request.use(
      (config) => {
        console.log(
          "Anlook PHP Request:",
          `${config.baseURL || ""}${config.url || ""}`,
        );

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Anlook PHP API Error:",
          error?.response?.status,
          error?.response?.data,
        );

        return Promise.reject(error);
      },
    );

    return instance;
  }, [anlookBaseUrl, anlookBasicAuth]);

  const seobixApi = useMemo(() => {
    const instance = axios.create({
      baseURL: seobixBaseUrl,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(seobixBasicAuth
          ? {
              Authorization: seobixBasicAuth,
            }
          : {}),
      },
      timeout: 30000,
    });

    instance.interceptors.request.use(
      (config) => {
        console.log(
          "Seobix Request URL:",
          `${config.baseURL || ""}${config.url || ""}`,
        );

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Seobix API Error:",
          error?.response?.status,
          error?.response?.data,
        );

        return Promise.reject(error);
      },
    );

    return instance;
  }, [seobixBaseUrl, seobixBasicAuth]);

  // ================= ANLOOK ADMIN / VENDOR API =================

  const anlookAdminApi = useMemo(() => {
    const instance = axios.create({
      baseURL: anlookAdminBaseUrl,
      headers: {
        Accept: "*/*",
        "Content-Type": "application/json",
        ...(seobixBasicAuth
          ? {
              Authorization: seobixBasicAuth,
            }
          : {}),
        "X-App-Name": appName || "Anlook",
      },
      timeout: 30000,
    });

    instance.interceptors.request.use(
      (config) => {
        if (userID) {
          config.headers["USER-ID"] = String(userID);
          config.headers["X-User-Id"] = String(userID);
        }

        console.log(
          "Anlook Admin Request:",
          `${config.baseURL || ""}${config.url || ""}`,
        );

        return config;
      },
      (error) => Promise.reject(error),
    );

    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error(
          "Anlook Admin API Error:",
          error?.response?.status,
          error?.response?.data,
        );

        return Promise.reject(error);
      },
    );

    return instance;
  }, [anlookAdminBaseUrl, seobixBasicAuth, appName, userID]);

  // SeoBix  instance
  const seoApi = useMemo(() => {
    return axios.create({
      baseURL: baseURL2,
      headers: {
        accept: "*/*",
        "Content-Type": "application/json",
      },
      timeout: 30000,
    });
  }, [baseURL2]);

  // ================= BULK CATALOGUE SESSION CACHE =================

  const bulkCatalogRequestsRef = useRef(new Map());

  const getCatalogIdUSCodeCached = useCallback(
    async (usCode, { force = false } = {}) => {
      const code = String(usCode ?? "").trim();

      if (!code) {
        throw new Error("usCode is required");
      }

      // User-wise cache
      const cacheKey = `bulk_catalog_uscode_${userID ?? "guest"}_${code}`;

      // 1. Check sessionStorage
      if (!force) {
        try {
          const cached = sessionStorage.getItem(cacheKey);

          if (cached !== null) {
            // Preserve Axios-style response structure
            return { data: JSON.parse(cached) };
          }
        } catch (error) {
          console.error("Bulk catalogue cache error:", error);
          sessionStorage.removeItem(cacheKey);
        }
      }

      // 2. Prevent simultaneous duplicate API requests
      const pending = bulkCatalogRequestsRef.current.get(cacheKey);

      if (pending) {
        return pending;
      }

      // 3. Call actual API
      const request = (async () => {
        const response = await api.post("/Inventory/getCatalogIdUSCode", {
          usCode: code,
        });

        const payload = response?.data;
        const responseCode = String(payload?.responseCode ?? "");

        // Cache only successful API responses
        if (
          payload &&
          (!responseCode || ["00", "0", "Y"].includes(responseCode))
        ) {
          try {
            sessionStorage.setItem(cacheKey, JSON.stringify(payload));
          } catch (error) {
            console.error("Bulk catalogue cache save error:", error);
          }
        }

        return response;
      })();

      bulkCatalogRequestsRef.current.set(cacheKey, request);

      try {
        return await request;
      } finally {
        bulkCatalogRequestsRef.current.delete(cacheKey);
      }
    },
    [api, userID],
  );


  // ================= MUTE STATE =================

  const [isMuted, setIsMuted] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("isMuted")) || false;
    } catch {
      return false;
    }
  });

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
  };

  useEffect(() => {
    localStorage.setItem("isMuted", JSON.stringify(isMuted));
  }, [isMuted]);

  // ================= UNREAD ORDERS =================

  const [unreadOrders, setUnreadOrders] = useState(() => {
    try {
      const saved = localStorage.getItem("unreadOrders");
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Unread orders parse error:", error);
      localStorage.removeItem("unreadOrders");
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("unreadOrders", JSON.stringify(unreadOrders));
  }, [unreadOrders]);

  const addUnreadOrder = (order) => {
    if (!order) return;

    setUnreadOrders((prev) => {
      if (order?.orderid) {
        const orderExists = prev.some(
          (item) => item?.orderid === order?.orderid,
        );

        if (orderExists) return prev;
      }

      if (order?._uid) {
        const uidExists = prev.some((item) => item?._uid === order?._uid);

        if (uidExists) return prev;
      }

      return [...prev, order];
    });
  };

  const clearUnreadOrders = () => {
    setUnreadOrders([]);
    localStorage.removeItem("unreadOrders");
  };

  // ================= AUTH FUNCTIONS =================

  const login = () => {
    const productId = "1";
    const base = window.location.origin;
    const callbackUrl = `${base}/identity`;

    const redirectUrl =
      `https://accounts.bumppy.com/` +
      `?callback=${encodeURIComponent(callbackUrl)}` +
      `&product_id=${productId}`;

    window.location.href = redirectUrl;
  };

  const signUp = () => {
    const productId = "1";
    const base = window.location.origin;
    const callbackUrl = `${base}/identity`;

    const redirectUrl =
      `https://accounts.bumppy.com/` +
      `?callback=${encodeURIComponent(callbackUrl)}` +
      `&product_id=${productId}` +
      `&intent=signup`;

    window.location.href = redirectUrl;
  };

  // ================= GLOBAL STATES =================

  const [generatedHTML, setGeneratedHTML] = useState("");
  const [wsConnected, setWsConnected] = useState(false);
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false);

  // ================= CONTACTS =================

  const [contacts, setContacts] = useState([]);
  const [contactsFetched, setContactsFetched] = useState(false);

  // ================= AUDIENCE CACHE =================

  const getInitialAudienceGroups = () => {
    if (!userID) return [];

    const cacheKey = `audience_groups_${userID}`;

    try {
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData === null) return [];

      const parsedData = JSON.parse(cachedData);

      return Array.isArray(parsedData) ? parsedData : [];
    } catch (error) {
      console.error("Audience cache parse error:", error);
      sessionStorage.removeItem(cacheKey);
      return [];
    }
  };

  const hasAudienceCache = () => {
    if (!userID) return false;

    return sessionStorage.getItem(`audience_groups_${userID}`) !== null;
  };

  const [audienceGroups, setAudienceGroups] = useState(
    getInitialAudienceGroups,
  );

  const [audienceFetched, setAudienceFetched] = useState(hasAudienceCache);

  useEffect(() => {
    if (!userID) {
      setAudienceGroups([]);
      setAudienceFetched(false);
      return;
    }

    setAudienceGroups(getInitialAudienceGroups());
    setAudienceFetched(hasAudienceCache());
  }, [userID]);

  // ================= WHATSAPP =================

  const [whatsappNumbers, setWhatsappNumbers] = useState([]);
  const [whatsappFetched, setWhatsappFetched] = useState(false);

  // ================= CHAT CONTACTS =================

  const [chatContacts, setChatContacts] = useState([]);
  const [chatContactsFetched, setChatContactsFetched] = useState(false);

  // ================= FREE TRIAL =================

  const openFreeTrialModal = () => {
    if (user?.userid) return;
    setShowFreeTrialModal(true);
  };

  const closeFreeTrialModal = () => {
    localStorage.setItem("freeTrialClosed_global", "true");
    setShowFreeTrialModal(false);
  };

  useEffect(() => {
    const hasLoggedInUser = Boolean(user?.userid);
    const hasClosedBefore = localStorage.getItem("freeTrialClosed_global");

    if (hasLoggedInUser) {
      setShowFreeTrialModal(false);
      localStorage.setItem("freeTrialClosed_global", "true");
      return;
    }

    if (!hasClosedBefore) {
      const timer = setTimeout(() => {
        setShowFreeTrialModal(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user?.userid]);

  // ================= CONTEXT VALUE =================

  const contextValue = useMemo(
    () => ({
      // API configuration
      baseUrl,
      baseURL2,
      appName,
      basicAuth,
      authHeaders,
      api,
      seoApi,
      bumppyBaseUrl,
      bumppyBasicAuth,
      bumppyApi,
      anlookApi,
      anlookBaseUrl,
      anlookBasicAuth,
      seobixBaseUrl,
      seobixBasicAuth,
      seobixApi,
      anlookAdminBaseUrl,
      anlookAdminApi,

      getCatalogIdUSCodeCached,
      // Blog credentials
      USERNAME,
      PASSWORD,

      // Authentication
      login,
      signUp,
      user,
      userID,
      setUser,
      power,
      setPower,

      // Notification sound state
      isMuted,
      toggleMute,

      // HTML
      generatedHTML,
      setGeneratedHTML,

      // Orders
      unreadOrders,
      addUnreadOrder,
      clearUnreadOrders,

      // WebSocket
      wsConnected,
      setWsConnected,

      // Free trial
      openFreeTrialModal,
      closeFreeTrialModal,

      // Contacts
      contacts,
      setContacts,
      contactsFetched,
      setContactsFetched,

      // Audience
      audienceGroups,
      setAudienceGroups,
      audienceFetched,
      setAudienceFetched,

      // WhatsApp
      whatsappNumbers,
      setWhatsappNumbers,
      whatsappFetched,
      setWhatsappFetched,

      // Chat contacts
      chatContacts,
      setChatContacts,
      chatContactsFetched,
      setChatContactsFetched,
    }),
    [
      baseUrl,
      baseURL2,
      appName,
      basicAuth,
      authHeaders,
      seobixBaseUrl,
      seobixBasicAuth,
      seobixApi,
      anlookAdminBaseUrl,
      anlookAdminApi,
      api,
      seoApi,
      bumppyBaseUrl,
      bumppyBasicAuth,
      bumppyApi,
      anlookBaseUrl,
      anlookBasicAuth,
      anlookApi,
      USERNAME,
      PASSWORD,
      user,
      userID,
      power,
      isMuted,
      generatedHTML,
      unreadOrders,
      wsConnected,
      contacts,
      contactsFetched,
      audienceGroups,
      audienceFetched,
      whatsappNumbers,
      whatsappFetched,
      chatContacts,
      chatContactsFetched,
    ],
  );

  // ================= PROVIDER =================

  return (
    <GlobalContext.Provider value={contextValue}>
      <GlobalDataLoader />

      {children}

      <FreeTrial show={showFreeTrialModal} handleClose={closeFreeTrialModal} />
    </GlobalContext.Provider>
  );
};;

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);

  if (!context) {
    throw new Error("useGlobalContext must be used inside GlobalProvider");
  }

  return context;
};
